/* eslint-disable @typescript-eslint/no-explicit-any */
import { scaleLinear, scaleBand, axisBottom, axisLeft, max, Selection } from 'd3';
import { IDataPoint } from '../../../redux/itemDetail/models';
import gaEvents from '../../../shared/analyticsEvents';

export const renderChart = (
  svg: Selection<SVGSVGElement, unknown, null, undefined>,
  svgWidth: number,
  svgHeight: number,
  yAxisTitle: string,
  data: IDataPoint[]
): void => {
  data = data.reverse();
  // value accessors
  const xValue = (d: IDataPoint) => d.YValues[0];
  const yValue = (d: IDataPoint) => d.AxisLabel;
  const dataSize = data.length;

  // set svg props - dimensions hard coded for now
  svg.style('background', '#ffffff');
  const chartYAxisMinInterval = 0;
  const chartYAxisMaxInterval = 100;

  // dynamic sizing
  const maxLabelSize = max(data.map(d => d.AxisLabel.length));
  const margin = {
    top: 20,
    right: (!!maxLabelSize && maxLabelSize >= 3) ? 20 : 40,
    bottom: 50,
    left: maxLabelSize ?
      (maxLabelSize < 3) ? 30 : maxLabelSize * 10 :
      1
  };
  const innerWidth = svgWidth - margin.left - margin.right;
  const innerHeight = svgHeight - margin.top - margin.bottom;

  // scale x-axis
  const xScale = scaleLinear()
    .domain([chartYAxisMinInterval, chartYAxisMaxInterval])
    .range([0, innerWidth]);

  // scale y-axis
  const yScale = scaleBand()
    .domain(data.map(yValue))
    .range([0, innerHeight])
    .paddingInner(0.3)
    .paddingOuter(0.3);

  // imaginary scale y-axis
  const yScaleDiv = scaleBand()
    .domain(data.filter( ( d: IDataPoint, i: number ) => i !== data.length - 1).map(yValue))
    .range([0, innerHeight])
    .paddingInner(1)
    .paddingOuter(1.1);

  // create group element container - this is what we'll append to
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`);

  // create bar chart title
  g.append('text')
    .attr('x', (innerWidth / 2) )
    .attr('y', (innerHeight + ( (margin.bottom + margin.top) / 2)))
    .style('font', '16px Arial, Helvetica, sans-serif')
    .attr('text-anchor', 'middle')
    .text(yAxisTitle);

  // create vertical gridlines
  g.append('g').call(axisBottom(xScale)
    .tickSize(-innerHeight)
  )
    .attr('transform', `translate(0, ${innerHeight})`)
    .call( (s: any) => s.selectAll('.tick text').remove())
    .selectAll('.tick line')
    .attr('stroke', '#dfdfdf')
    .attr('stroke-width', '1');

  // append data to chart, create rectangles
  g.selectAll('rect').data(data)
    .enter().append('rect')
    .attr('class', 'sec-rect')
    .attr('y', (d:IDataPoint) => {
      const val = yScale(yValue(d));
      return val ? val : 0;
    })
    .attr('x', '1') // account for stroke-width
    .attr('width', (d:IDataPoint) => xScale(xValue(d)))
    .attr('height', yScale.bandwidth())
    .style('font', '12px Arial, Helvetica, sans-serif')
    .style('border-radius', '0')
    .attr('fill', '#ffffff')
    .attr('stroke', '#ffffff')
    .attr('stroke-width', '1')
    .attr('fill-opacity', '1')
    .attr('stroke-opacity', '1');

  // create one more set of rectangles right ontop
  g.selectAll('rect:not(.sec-rect)').data(data)
    .enter().append('rect')
    .attr('y', (d: IDataPoint) => {
      const val = yScale(yValue(d));
      return val ? val : 0;
    })
    .attr('x', '1')
    .attr('width', (d: IDataPoint) => xScale(xValue(d)))
    .attr('height', yScale.bandwidth())
    .style('font', '12px Arial, Helvetica, sans-serif')
    .style('border-radius', '0')
    .attr('fill', '#41639E')
    .attr('stroke', '#41639E')
    .attr('stroke-width', '1')
    .attr('fill-opacity', '1')
    .attr('stroke-opacity', '1');


  // create y-axis minor ticks
  g.append('g').call(axisLeft(yScaleDiv))
    .style('font', '12px Arial, Helvetica, sans-serif')
    .call( (s: any) => s.select('.domain').remove())
    .call( (s: any) => s.selectAll('.tick text').remove())
    .selectAll('.tick line')
    .attr('stroke', '#8e8e8e')
    .attr('stroke-width', '1');

  // create y-axis minor gridlines
  g.append('g').call(axisLeft(yScaleDiv)
    .tickSizeInner(-innerWidth)
    .tickSizeOuter(-innerWidth)
  )
    .style('font', '12px Arial, Helvetica, sans-serif')
    .call( (s: any) => s.selectAll('.tick text').remove())
    .selectAll('.tick line')
    .attr('stroke', '#dfdfdf')
    .attr('stroke-width', '1');

  // create top horizontal gridline
  g.select('.domain')
    .attr('stroke', '#dfdfdf')
    .attr('stroke-width', '1');


  // place y-axis on left side
  g.append('g').call(axisLeft(yScale)
    .tickSize(10)
  )
    .style('font', '12px Arial, Helvetica, sans-serif')
    .selectAll('.tick line').data(data)
    .attr('transform', (d: IDataPoint) => `translate(${xScale(xValue(d)) + 11}, 0)`)
    .attr('stroke-width', 2)
    .attr('stroke', '#8e8e8e');

  // Append statValueLabels to end of rect
  g.append('g').selectAll('text')
    .data(data).enter()
    .append('text')
    .attr('class', 'stat-value-label')
    .attr('x', (d: IDataPoint) => xScale(xValue(d)) + 14)
    .attr('y', (d: IDataPoint) => {
      const val = yScale(yValue(d));
      return val ? val + yScale.bandwidth() - (yScale.bandwidth() / dataSize) : 0;
    })
    .style('font', '12px Arial, Helvetica, sans-serif')
    .style('color', '#333333')
    .text((d: IDataPoint) => d.Label);


  // place x-axis on bottom
  g.append('g').call(axisBottom(xScale))
    .attr('transform', `translate(0, ${innerHeight})`)
    .style('font', '12px Arial, Helvetica, sans-serif')
    .selectAll('.tick line')
    .attr('stroke', '#8e8e8e')
    .attr('stroke-width', '1');

  // Grey origin axes
  g.selectAll('.domain')
    .attr('stroke', '#8e8e8e')
    .attr('stroke-width', '1');

};

/**
 * Grabs section-to-print, anchors it to the body for absolute positioning,
 * hides it while print window is open. After the user prints or cancels,
 * the section is set to visible, and re-attached to original position
 */
export const printHandler = (): void => {
  const printEl = document.getElementById('pills-tabContent');
  const printAnchor = document.getElementById('print-anchor');
  const itemDetailPrint = document.getElementsByClassName('item-detail-print');
  if (printEl && printAnchor) {
    const additionalMat = document.querySelectorAll('.item-detail .stimulus');
    const additionalMatBtns = document.querySelector('.question-content--additional-materials');
    if (additionalMat.length > 0 && additionalMatBtns) {
      
      additionalMatBtns.className += ' d-none';
      additionalMat.forEach((el) => {
        const classList = el.className.split(' ');
       
        if (classList.includes('d-none')) {
          el.className = classList.filter(cls => cls !== 'd-none').join(' ');
        }
      });
    }
    printEl.style.visibility = 'hidden';
    if (printEl.contains(itemDetailPrint[0]?.parentElement)) {
      for (let i =0; i < itemDetailPrint.length; i++){
        const notToBeInPrint = itemDetailPrint[i]?.parentElement;
        if (notToBeInPrint){
          notToBeInPrint.style.display = 'none';
        }
      }
    }
  
    document.body.appendChild(printEl);
    window.print(); // thread blocking
    if (additionalMat.length > 0 && additionalMatBtns) {
      additionalMatBtns.className = additionalMatBtns.className.split(' ').filter(cls => cls !== 'd-none').join(' ');
      additionalMat.forEach(el => {
        el.className += ' d-none';
      });
    }
    printEl.style.visibility = 'visible';
    document.body.removeChild(printEl);
    printAnchor.appendChild(printEl);
    for (let i =0; i < itemDetailPrint.length; i++){
      const returnedToBeInPrint = itemDetailPrint[i]?.parentElement;
      if (returnedToBeInPrint){
        returnedToBeInPrint.style.display = 'block';
      }
    }
  }
};

/**
 *
 * @param nodelist list of <stimlabel> nodes to use for number of additional materials and label of buttons
 * @param callback Callback to manipulate DOM to toggle view of additional materials
 * @param SAcontroltext button label text for select all option.
 * @returns JSX.Element Array
 */
export const setAdditionalMaterials = (nodelist: NodeListOf<Element>|null, callback: (ind: number) => void, SAcontroltext: string | undefined):JSX.Element[] => {
  if (!nodelist){
    return [];
  }
  const buttonArray = [];
  const sacontroltext = SAcontroltext || 'Select All';

  // get max string length on nodelist[].innerHTML to set width of all buttons.
  let maxl = sacontroltext.length;
  for (let x=0; x < nodelist.length; x++) {
    if(nodelist[x].innerHTML.length > maxl){
      maxl = nodelist[x].innerHTML.length;
    }
  }

  const buttonwidth = maxl * 10;

  for (let x=0; x < nodelist.length; x++) {
    buttonArray.push(
      <button key={`add-mat-${x}`} id={`add-mat-${x}`} className='additional-materials--button' style={{width:`${buttonwidth-50}px`}}
        onClick={() => { callback(x); gaEvents.onGA4ViewAddMaterials(nodelist[x].innerHTML);}}>
        {nodelist[x].innerHTML}
      </button>
    );
  }
  if(nodelist.length > 1){
    buttonArray.push(
      <button key={`add-mat-${99}`} id={`add-mat-${99}`} className='additional-materials--button' style={{width:`${buttonwidth-30}px`}}
        onClick={() => { callback(99); gaEvents.onGA4ViewAddMaterials(sacontroltext);}}>
        {sacontroltext}
      </button>
    );
  }
  return buttonArray;
};

/**
 *
 * @param ind Toggles additional material based on its index in the list. ind 99 selectAll
 */
export const toggleAdditionalMaterial = (ind: number):void => {
  const additionalMat = document.querySelectorAll('.item-detail .stimulus');
  const btnid = 'add-mat-' + ind;
  const btn = document.getElementById(btnid);

  //SelectAll ind === 99
  if (btn && ind === 99)
  {
    // toggle all on
    if(btn.className == 'additional-materials--button') {
      btn.className = 'additional-materials--button active';
      for (let i = 0; i < additionalMat.length; i++) {
        const btn = document.getElementById('add-mat-' + i);
        if(btn) {
          btn.className = 'additional-materials--button active';
        }
        const additionalMaterial = additionalMat.item(i);
        const originalClasses = additionalMaterial.className;
        const classes = originalClasses.split(' ');
        additionalMaterial.className = classes.filter( cls => cls !== 'd-none').join(' ');
      }
    // toggle all off
    } else {
      btn.className = 'additional-materials--button';
      for (let i = 0; i < additionalMat.length; i++) {
        const btn = document.getElementById('add-mat-' + i);
        if(btn) {
          btn.className = 'additional-materials--button';
        }
        const additionalMaterial = additionalMat.item(i);
        const originalClasses = additionalMaterial.className;
        const classes = originalClasses.split(' ');
        if (classes.filter(cls => cls === 'd-none').length === 0 ) {
          additionalMaterial.className = originalClasses + ' d-none';
        }
      }
    }
    return;
  }

  if (additionalMat && ind > -1 && ind < additionalMat.length) {
    // if select all is enabled keep ind on.
    let saenabled = false;
    //switch off select all
    if(additionalMat.length > 1){
      const btn = document.getElementById('add-mat-' + 99);
      if(btn){
        if(btn.className == 'additional-materials--button active')
        {
          saenabled = true;
        }
        btn.className = 'additional-materials--button';
      }
    }

    for (let i = 0; i < additionalMat.length; i++)
    {
      if(i === ind)
      {
        //if clicked button toggle on off
        const btn = document.getElementById('add-mat-' + ind);
        if (btn){
          if(btn.className == 'additional-materials--button' || saenabled) {
            btn.className = 'additional-materials--button active';
          } else {
            btn.className = 'additional-materials--button';
          }
        }
        // toggle show hide additional material
        const additionalMaterial = additionalMat.item(ind);
        const originalClasses = additionalMaterial.className;
        const classes = originalClasses.split(' ');
        if (classes.filter(cls => cls === 'd-none').length === 0 && !saenabled) {
          additionalMaterial.className = originalClasses + ' d-none';
        } else {
          additionalMaterial.className = classes.filter( cls => cls !== 'd-none').join(' ');
        }

      }else{ // all others toggle off button and hide additionalmaterial
        const btn = document.getElementById('add-mat-' + i);
        if(btn){
          btn.className = 'additional-materials--button';
        }
        const additionalMaterial = additionalMat.item(i);
        const originalClasses = additionalMaterial.className;
        const classes = originalClasses.split(' ');
        if (classes.filter(cls => cls === 'd-none').length === 0 ) {
          additionalMaterial.className = originalClasses + ' d-none';
        }
      }
    }
  }
};

/**
 * Grabs all AdditionalMaterial buttons and toggles them off.
 */
export const toggleAdditionalMaterialAllOff = ():void => {
  const btnid = 'add-mat-' + 99;
  const btn = document.getElementById(btnid);
  const additionalMat = document.querySelectorAll('.item-detail .stimulus');
  if (btn){
    btn.className = 'additional-materials--button';
  }

  for (let i = 0; i < additionalMat.length; i++) {
    const btn = document.getElementById('add-mat-' + i);
    if(btn) {
      btn.className = 'additional-materials--button';
    }
    const additionalMaterial = additionalMat.item(i);
    const originalClasses = additionalMaterial.className;
    const classes = originalClasses.split(' ');
    if (classes.filter(cls => cls === 'd-none').length === 0 ) {
      additionalMaterial.className = originalClasses + ' d-none';
    }
  }
};

export const showPerformancePanel = () : void => {
  openShowQuestionPanel();
  const sidePanelButton = document.querySelector('.nqt-xp--accordion-button') as HTMLButtonElement;
  // check to see if the side button is disabled, should not happen
  if (!sidePanelButton.classList.contains('collapsed')) { return;}
  sidePanelButton.click();
};

export const openShowQuestionPanel = () : void => {
  const sidePanelButton = document.getElementById('show-hide-qp') as HTMLButtonElement;
  // check to see if the side button is disabled, should not happen
  if (!sidePanelButton.classList.contains('nqt-sq--sp-showing')) {
    sidePanelButton.click();
  }
};



